import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import moment, { Moment } from 'moment'
import { Pagination } from 'antd'
import { ArrowLongRightIcon, DocumentChartBarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

import { useGetCheckupListResource } from 'api/checkups/getCheckupListResource'
import { useExportFile } from 'hooks'
import { notification } from 'helpers'
import { CheckupLogListItem } from 'types/domain/CheckupLog'
import { PageWrapper } from 'ui'
import { Button, DateRangePicker, Header, Input, Table } from 'ui-v2'

const PAGE_SIZE = 20

const normalizeCodes = (codes?: string[] | string | null) => {
  if (Array.isArray(codes)) {
    return codes.filter(Boolean)
  }

  if (typeof codes === 'string') {
    return codes
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  return []
}

const formatStay = (item: Pick<CheckupLogListItem, 'from_date' | 'to_date'>) =>
  `${moment(item.from_date).format('DD.MM.YYYY')} - ${moment(item.to_date).format('DD.MM.YYYY')}`

const formatGuests = (item: Pick<CheckupLogListItem, 'adults' | 'children'>) =>
  `${item.adults} adults${item.children ? `, ${item.children} children` : ''}`

const formatCorporateCodes = (item: Pick<CheckupLogListItem, 'corporate_codes'>) => {
  const codes = normalizeCodes(item.corporate_codes)

  return codes.length > 0 ? codes.join(', ') : '-'
}

const formatRequester = (item: Pick<CheckupLogListItem, 'requester_name' | 'requester_email'>) =>
  item.requester_name?.trim() || item.requester_email?.trim() || '-'

const parseQueryDate = (value?: string | null): Moment | null => {
  if (!value) {
    return null
  }

  const parsedDate = moment(value, 'YYYY-MM-DD', true)

  return parsedDate.isValid() ? parsedDate : null
}

const CheckupLogsList = () => {
  const { push, location } = useHistory()
  const { search, pathname } = useLocation()
  const urlQuery = useMemo(() => new URLSearchParams(search), [search])
  const parsedPage = Number(urlQuery.get('page') || 1)
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1
  const activeHotelName = urlQuery.get('hotel_name')?.trim() || ''
  const activeRequester = urlQuery.get('requester')?.trim() || ''
  const activeDateFrom = urlQuery.get('date_from') || ''
  const activeDateTo = urlQuery.get('date_to') || ''
  const [hotelNameValue, setHotelNameValue] = useState(activeHotelName)
  const [requesterValue, setRequesterValue] = useState(activeRequester)
  const [dateRange, setDateRange] = useState<[Moment | null, Moment | null]>([
    parseQueryDate(activeDateFrom),
    parseQueryDate(activeDateTo),
  ])
  const { isLoading: isExporting, saveFile } = useExportFile()

  useEffect(() => {
    setHotelNameValue(activeHotelName)
  }, [activeHotelName])

  useEffect(() => {
    setRequesterValue(activeRequester)
  }, [activeRequester])

  useEffect(() => {
    setDateRange([parseQueryDate(activeDateFrom), parseQueryDate(activeDateTo)])
  }, [activeDateFrom, activeDateTo])

  const { data, isLoading, isFetching } = useGetCheckupListResource({
    query: {
      page,
      length: PAGE_SIZE,
      hotel_name: activeHotelName || undefined,
      requester: activeRequester || undefined,
      date_from: activeDateFrom || undefined,
      date_to: activeDateTo || undefined,
    },
    queryOptions: {
      keepPreviousData: true,
      onError: (error) => notification.error({ message: error.message }),
    },
  })

  const handleSetPage = useCallback(
    (nextPage: number) => {
      const nextQuery = new URLSearchParams(search)

      if (nextPage > 1) {
        nextQuery.set('page', `${nextPage}`)
      } else {
        nextQuery.delete('page')
      }

      push({
        pathname,
        search: nextQuery.toString() ? `?${nextQuery.toString()}` : '',
      })
    },
    [pathname, push, search],
  )

  const handleSubmitSearch = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const nextQuery = new URLSearchParams(search)
      const normalizedHotelName = hotelNameValue.trim()
      const normalizedRequester = requesterValue.trim()

      if (normalizedHotelName) {
        nextQuery.set('hotel_name', normalizedHotelName)
      } else {
        nextQuery.delete('hotel_name')
      }

      if (normalizedRequester) {
        nextQuery.set('requester', normalizedRequester)
      } else {
        nextQuery.delete('requester')
      }

      if (dateRange[0] && dateRange[1]) {
        nextQuery.set('date_from', dateRange[0].format('YYYY-MM-DD'))
        nextQuery.set('date_to', dateRange[1].format('YYYY-MM-DD'))
      } else {
        nextQuery.delete('date_from')
        nextQuery.delete('date_to')
      }

      nextQuery.delete('page')

      push({
        pathname,
        search: nextQuery.toString() ? `?${nextQuery.toString()}` : '',
      })
    },
    [dateRange, hotelNameValue, pathname, push, requesterValue, search],
  )

  const handleClearSearch = useCallback(() => {
    setHotelNameValue('')
    setRequesterValue('')
    setDateRange([null, null])

    const nextQuery = new URLSearchParams(search)
    nextQuery.delete('hotel_name')
    nextQuery.delete('requester')
    nextQuery.delete('page')
    nextQuery.delete('date_from')
    nextQuery.delete('date_to')

    push({
      pathname,
      search: nextQuery.toString() ? `?${nextQuery.toString()}` : '',
    })
  }, [pathname, push, search])

  const handleOpenDetails = useCallback(
    (checkupId: number) => {
      push(`/admin/logs/${checkupId}${location.search}`)
    },
    [location.search, push],
  )

  const handleExport = useCallback(() => {
    const fileNameParts = ['Rate check history']

    if (activeHotelName) {
      fileNameParts.push(`Hotel ${activeHotelName}`)
    }

    if (activeRequester) {
      fileNameParts.push(`Requester ${activeRequester}`)
    }

    if (activeDateFrom && activeDateTo) {
      fileNameParts.push(
        `${moment(activeDateFrom).format('YYYY-MM-DD')} to ${moment(activeDateTo).format('YYYY-MM-DD')}`,
      )
    }

    fileNameParts.push(moment().format('YYYY-MM-DD HH.mm'))

    return saveFile('/exports/checkup-logs', `${fileNameParts.join(' - ')}.xlsx`, {
      type: 'checkup-logs',
      hotel_name: activeHotelName || undefined,
      requester: activeRequester || undefined,
      date_from: activeDateFrom || undefined,
      date_to: activeDateTo || undefined,
    })
  }, [activeDateFrom, activeDateTo, activeHotelName, activeRequester, saveFile])

  const hasActiveFilters =
    !!activeHotelName ||
    !!hotelNameValue ||
    !!activeRequester ||
    !!requesterValue ||
    !!activeDateFrom ||
    !!activeDateTo ||
    !!dateRange[0] ||
    !!dateRange[1]

  return (
    <>
      <Header>
        <h1 className="text-gray-800 text-3xl font-semibold leading-9 mb-0">Rate check history</h1>
      </Header>

      <form
        className="p-4 bg-bg-accent-7 flex flex-col gap-3 md:px-7 lg:flex-row lg:flex-wrap lg:items-start"
        onSubmit={handleSubmitSearch}
      >
        <Input
          className="lg:flex-1 lg:min-w-[220px]"
          label="Hotel name"
          value={hotelNameValue}
          onChange={(value) => setHotelNameValue(value || '')}
        />

        <Input
          className="lg:flex-1 lg:min-w-[220px]"
          label="Requester name / email"
          value={requesterValue}
          onChange={(value) => setRequesterValue(value || '')}
        />

        <DateRangePicker
          className="lg:min-w-[320px]"
          labels={['Created from', 'Created to']}
          value={dateRange}
          onChange={(dates) => {
            if (!dates) {
              setDateRange([null, null])
              return
            }

            setDateRange(dates)
          }}
          isClearable
        />

        <Button
          htmlType="submit"
          size="large"
          variant="primary"
          className="lg:w-auto"
          isLoading={isFetching}
          childrenClassName="inline-flex h-full w-full items-center justify-center gap-2"
        >
          <MagnifyingGlassIcon className="w-5" />
          <span>Search</span>
        </Button>

        <Button
          size="large"
          variant="default"
          className="lg:w-auto"
          onClick={handleClearSearch}
          isDisabled={!hasActiveFilters}
        >
          Clear
        </Button>

        <Button
          size="large"
          variant="secondary"
          className="lg:w-auto"
          onClick={handleExport}
          isLoading={isExporting}
          isDisabled={!data?.meta.total}
          childrenClassName="inline-flex h-full w-full items-center justify-center gap-2"
        >
          <DocumentChartBarIcon className="w-5" />
          <span>Export</span>
        </Button>
      </form>

      <div className="bg-[#f8f9fe]">
        <PageWrapper className="!mt-6 !pr-0">
          <div className="bg-white px-4 pr-[30px] relative">
            {(isLoading || isFetching) && (
              <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-50 opacity-35 z-10">
                <span className="absolute top-1/2 left-1/2 w-12 h-12 inline-flex items-center justify-center -mt-6 -ml-6">
                  <svg
                    aria-hidden="true"
                    className="w-10 h-10 text-gray-200 animate-spin fill-blue-800"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </span>
              </div>
            )}

            <div className="flex items-center mb-6 pt-4">
              <div className="text-base text-gray-500">No. of rate checks: {data?.meta.total || 0}</div>
            </div>

            <div className="flex flex-col gap-2 xl:hidden pb-4">
              {(data?.data || []).map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 border border-indigo-100 rounded p-3 even:bg-bg-light"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">Log #</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{item.id}</div>
                    </div>
                    <Button
                      variant="secondary"
                      className="border-none"
                      rounded
                      onClick={() => handleOpenDetails(item.id)}
                      childrenClassName="inline-flex h-full w-full items-center justify-center"
                    >
                      <ArrowLongRightIcon className="w-5" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">Hotel</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{item.hotel_name || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">Requester</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{formatRequester(item)}</div>
                    </div>
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">GDS</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{item.gds_name || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">Property ID</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{item.property_id || '-'}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs leading-none font-normal text-gray-500">Stay</div>
                    <div className="text-base leading-6 font-normal text-gray-700">{formatStay(item)}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">Guests</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{formatGuests(item)}</div>
                    </div>
                    <div>
                      <div className="text-xs leading-none font-normal text-gray-500">RAC</div>
                      <div className="text-base leading-6 font-normal text-gray-700">{formatCorporateCodes(item)}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs leading-none font-normal text-gray-500">Created</div>
                    <div className="text-base leading-6 font-normal text-gray-700">
                      {moment(item.created_at).format('DD.MM.YYYY HH:mm')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden xl:block w-full">
              <Table className="px-4 table-fixed">
                <thead>
                  <Table.Tr>
                    <Table.Th className="w-[90px]">Log #</Table.Th>
                    <Table.Th>Hotel</Table.Th>
                    <Table.Th>Requester</Table.Th>
                    <Table.Th>GDS</Table.Th>
                    <Table.Th className="w-[120px]">Property ID</Table.Th>
                    <Table.Th>Stay</Table.Th>
                    <Table.Th>Guests</Table.Th>
                    <Table.Th>RAC</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th className="w-[62px] text-center !px-4"></Table.Th>
                  </Table.Tr>
                </thead>
                <tbody>
                  {(data?.data || []).map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td className="w-[90px]">{item.id}</Table.Td>
                      <Table.Td>{item.hotel_name || '-'}</Table.Td>
                      <Table.Td>{formatRequester(item)}</Table.Td>
                      <Table.Td>{item.gds_name || '-'}</Table.Td>
                      <Table.Td className="w-[120px]">{item.property_id || '-'}</Table.Td>
                      <Table.Td>{formatStay(item)}</Table.Td>
                      <Table.Td>{formatGuests(item)}</Table.Td>
                      <Table.Td>{formatCorporateCodes(item)}</Table.Td>
                      <Table.Td>{moment(item.created_at).format('DD.MM.YYYY HH:mm')}</Table.Td>
                      <Table.Td className="w-[62px] !px-4 !align-middle">
                        <div className="flex items-center justify-center">
                          <Button
                            variant="secondary"
                            className="border-none"
                            rounded
                            onClick={() => handleOpenDetails(item.id)}
                            childrenClassName="inline-flex h-full w-full items-center justify-center"
                          >
                            <ArrowLongRightIcon className="w-5" />
                          </Button>
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {!isLoading && (data?.data || []).length === 0 && (
              <div className="text-center text-base text-gray-500 p-5">No rate check history</div>
            )}
          </div>

          <div className="flex items-center justify-center pt-5">
            <Pagination
              current={data?.meta.current_page || page}
              total={data?.meta.total || 1}
              onChange={handleSetPage}
              showSizeChanger={false}
              hideOnSinglePage
              pageSize={data?.meta.per_page || PAGE_SIZE}
            />
          </div>
        </PageWrapper>
      </div>
    </>
  )
}

export default CheckupLogsList
